import React, { useState, useEffect } from 'react';
import { Search, ExternalLink, Star, Calendar, Download, RefreshCw, ChevronDown, ChevronUp, Plus, Edit2, Trash2, Users, Globe, Lock, Package, Server, Tag, Zap, FileText, AlertCircle, HardDrive, Copy, Check } from 'lucide-react';
import dockerHubService from '../services/dockerHubService';
import registryService from '../services/registryService';
import * as imageService from '../services/imageService';
import weightService from '../services/weightService';
import { useAuth } from '../hooks/useAuth';
import ImageFormModal from './ImageFormModal';
import ImageSharingModal from './ImageSharingModal';
import OpenApiViewer from './OpenApiViewer';

const ImagePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('catalog'); // 'catalog' or 'dockerhub'

  // Curated catalog state
  const [curatedImages, setCuratedImages] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [showImageForm, setShowImageForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [sharingImage, setSharingImage] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [viewingOpenApi, setViewingOpenApi] = useState(null);

  // Details expansion (imageId -> weights data)
  const [expandedDetails, setExpandedDetails] = useState(null);
  const [imageWeights, setImageWeights] = useState({});
  const [weightsCopied, setWeightsCopied] = useState(null);

  // Conversion step tracking (imageId -> { status, steps })
  const [conversionProgress, setConversionProgress] = useState({});

  // Registry browsing state
  const [registries, setRegistries] = useState([]);
  const [selectedRegistry, setSelectedRegistry] = useState(null);
  const [registryRepos, setRegistryRepos] = useState([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState(null);
  const [registryTags, setRegistryTags] = useState({});
  const [openRegistryDropdown, setOpenRegistryDropdown] = useState(null);

  // Docker Hub state
  const [dockerHubRepositories, setDockerHubRepositories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [currentMode, setCurrentMode] = useState('search');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [repoTags, setRepoTags] = useState({});


  useEffect(() => {
    setIsAuthenticated(dockerHubService.isAuthenticated());
    fetchCuratedImages();
    fetchRegistries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Curated catalog functions
  const fetchCuratedImages = async () => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const data = await imageService.listCuratedImages();
      if (data.success) setCuratedImages(data.images || []);
    } catch (err) {
      setCatalogError(err.message);
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleCreateImage = async (imageData) => {
    await imageService.createCuratedImage(imageData);
    fetchCuratedImages();
  };

  const handleUpdateImage = async (imageData) => {
    await imageService.updateCuratedImage(editingImage.id, imageData);
    setEditingImage(null);
    fetchCuratedImages();
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this curated image?')) return;
    try {
      await imageService.deleteCuratedImage(imageId);
      fetchCuratedImages();
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  const getVisibilityIcon = (visibility) => {
    if (visibility === 'public') return <Globe size={12} className="text-blue-600" />;
    if (visibility === 'semi_private') return <Users size={12} className="text-orange-600" />;
    return <Lock size={12} className="text-gray-500" />;
  };

  // TCF conversion
  const handleConvertToTcf = async (imageId) => {
    if (!window.confirm('Convert this image to TCF? This will pull the image, extract the OpenAPI spec, and push a TCF-enabled version.')) return;
    try {
      await imageService.convertToTcf(imageId);
      fetchCuratedImages();
    } catch (err) {
      alert(`Conversion failed: ${err.message}`);
    }
  };

  // Toggle image details + fetch weights
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const toggleDetails = async (imageId) => {
    if (expandedDetails === imageId) {
      setExpandedDetails(null);
      return;
    }
    setExpandedDetails(imageId);
    if (!imageWeights[imageId]) {
      try {
        const res = await weightService.listWeightsByImage(imageId);
        if (res.success) {
          setImageWeights(prev => ({ ...prev, [imageId]: res.weights || [] }));
        }
      } catch (err) {
        console.error('Fetch image weights error:', err);
      }
    }
  };

  const copyWeightUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setWeightsCopied(url);
      setTimeout(() => setWeightsCopied(null), 2000);
    });
  };

  // Poll for converting images
  useEffect(() => {
    const convertingImages = curatedImages.filter(img => img.tcf_status === 'converting');
    if (convertingImages.length === 0) return;

    const poll = async () => {
      let changed = false;
      for (const img of convertingImages) {
        try {
          const result = await imageService.getConversionStatus(img.id);
          if (result.image && result.image.tcf_status !== 'converting') {
            changed = true;
            setConversionProgress(prev => { const next = { ...prev }; delete next[img.id]; return next; });
          } else if (result.conversion_status || result.steps) {
            setConversionProgress(prev => ({ ...prev, [img.id]: { status: result.conversion_status, steps: result.steps || [] } }));
          }
        } catch { /* ignore */ }
      }
      if (changed) fetchCuratedImages();
    };

    poll(); // poll immediately on mount
    const interval = setInterval(poll, 5000);

    return () => clearInterval(interval);
  }, [curatedImages]);

  // Registry browsing functions
  const fetchRegistries = async () => {
    try {
      const result = await registryService.listRegistries();
      if (result.success) setRegistries(result.registries || []);
    } catch (err) {
      console.error('Failed to fetch registries:', err);
    }
  };

  const browseRegistry = async (registry) => {
    setSelectedRegistry(registry);
    setRegistryLoading(true);
    setRegistryError(null);
    setRegistryRepos([]);
    setRegistryTags({});
    setOpenRegistryDropdown(null);
    try {
      const result = await registryService.browseRepositories(registry.id);
      if (result.success) {
        setRegistryRepos(result.repositories || []);
      } else {
        const debugInfo = result.debug ? '\n\nDebug: ' + JSON.stringify(result.debug, null, 2) : '';
        setRegistryError((result.error || 'Failed to browse registry') + debugInfo);
      }
    } catch (err) {
      setRegistryError(err.message || 'Failed to connect to registry');
    } finally {
      setRegistryLoading(false);
    }
  };

  const fetchRegistryTags = async (registryId, repoPath) => {
    if (registryTags[repoPath]) return;
    setRegistryTags(prev => ({ ...prev, [repoPath]: { loading: true } }));
    try {
      const result = await registryService.browseTags(registryId, repoPath);
      if (result.success) {
        setRegistryTags(prev => ({ ...prev, [repoPath]: result.tags || [] }));
      } else {
        setRegistryTags(prev => ({ ...prev, [repoPath]: { error: result.error } }));
      }
    } catch (err) {
      setRegistryTags(prev => ({ ...prev, [repoPath]: { error: err.message } }));
    }
  };

  const addRegistryImageToCatalog = (repo, tag) => {
    const dockerRef = tag ? `${selectedRegistry.server}/${repo}:${tag}` : `${selectedRegistry.server}/${repo}`;
    setPrefillData({
      name: repo.split('/').pop(),
      docker_image: dockerRef,
      registry_server: selectedRegistry.server,
      registry_username: selectedRegistry.username,
    });
    setEditingImage(null);
    setShowImageForm(true);
  };

  const toggleRegistryDropdown = async (registryId, repoPath) => {
    if (openRegistryDropdown === repoPath) {
      setOpenRegistryDropdown(null);
    } else {
      setOpenRegistryDropdown(repoPath);
      await fetchRegistryTags(registryId, repoPath);
    }
  };

  const browseDockerHub = () => {
    if (selectedRegistry?.id === 'dockerhub') return;
    setSelectedRegistry({ id: 'dockerhub', name: 'Docker Hub', server: 'docker.io' });
    setRegistryRepos([]);
    setRegistryTags({});
    setOpenRegistryDropdown(null);
  };

  const addDockerHubImageToCatalog = (repoName, tag) => {
    const dockerRef = tag ? `${repoName}:${tag}` : repoName;
    setPrefillData({
      name: repoName.split('/').pop(),
      docker_image: dockerRef,
    });
    setEditingImage(null);
    setShowImageForm(true);
  };

  // Docker Hub functions (unchanged)
  const loadUserRepositories = async (page = 1, resetResults = true) => {
    if (!dockerHubService.isAuthenticated()) {
      setError('Please connect your Docker Hub account in Settings first');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await dockerHubService.getRepositories(null, page, 100);
      if (result.success) {
        if (resetResults) setDockerHubRepositories(result.repositories);
        else setDockerHubRepositories(prev => [...prev, ...result.repositories]);
        setTotalCount(result.count || 0);
        setHasNextPage(!!result.next);
        setHasPreviousPage(!!result.previous);
        setCurrentPage(page);
        setCurrentMode('repos');
      } else {
        setError(result.error || 'Failed to load repositories');
      }
    } catch (err) {
      setError('Failed to connect to Docker Hub');
    } finally {
      setLoading(false);
    }
  };

  const searchDockerHub = async (page = 1, resetResults = true) => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await dockerHubService.searchRepositories(searchQuery, 100, page);
      if (result.success) {
        if (resetResults) setDockerHubRepositories(result.repositories);
        else setDockerHubRepositories(prev => [...prev, ...result.repositories]);
        setTotalCount(result.count || 0);
        setHasNextPage(!!result.next);
        setHasPreviousPage(!!result.previous);
        setCurrentPage(page);
        setCurrentMode('search');
      } else {
        setError(result.error || 'Search failed');
      }
    } catch (err) {
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  const sortRepositories = (repositories, sortBy) => {
    const sorted = [...repositories];
    switch (sortBy) {
      case 'stars': return sorted.sort((a, b) => (b.star_count || 0) - (a.star_count || 0));
      case 'pulls': return sorted.sort((a, b) => (b.pull_count || 0) - (a.pull_count || 0));
      case 'updated': return sorted.sort((a, b) => new Date(b.last_updated || 0) - new Date(a.last_updated || 0));
      case 'name': return sorted.sort((a, b) => a.name.localeCompare(b.name));
      default: return sorted;
    }
  };

  const fetchRepositoryTags = async (repo) => {
    const repoKey = repo.name;
    if (repoTags[repoKey]) return repoTags[repoKey];
    try {
      const parts = repo.name.split('/');
      let namespace, repository;
      if (parts.length === 2) {
        [namespace, repository] = parts;
      } else {
        const username = dockerHubService.getUsername();
        if (username && dockerHubService.isAuthenticated()) {
          namespace = username;
          repository = repo.name;
        } else {
          namespace = 'library';
          repository = repo.name;
        }
      }
      const result = await dockerHubService.getRepositoryTags(namespace, repository);
      if (result.success) {
        setRepoTags(prev => ({ ...prev, [repoKey]: result.tags }));
        return result.tags;
      } else {
        let errorType = 'error';
        if (result.error && result.error.includes('404')) {
          errorType = !dockerHubService.isAuthenticated() ? 'private' : 'notfound';
        }
        setRepoTags(prev => ({ ...prev, [repoKey]: { error: errorType } }));
        return { error: errorType };
      }
    } catch (error) {
      setRepoTags(prev => ({ ...prev, [repoKey]: { error: 'error' } }));
      return { error: 'error' };
    }
  };

  const toggleDropdown = async (repo) => {
    const repoKey = repo.name;
    if (openDropdown === repoKey) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(repoKey);
      if (!repoTags[repoKey]) await fetchRepositoryTags(repo);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Images</h2>
      </div>

      {/* Tab Bar */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'catalog'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Package size={14} className="inline mr-1.5" />
          Curated Catalog
        </button>
        <button
          onClick={() => setActiveTab('browse')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'browse'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Search size={14} className="inline mr-1.5" />
          Browse
        </button>
      </div>

      {/* Curated Catalog Tab */}
      {activeTab === 'catalog' && (
        <>
          {catalogLoading && (
            <div className="flex justify-center py-8">
              <RefreshCw className="animate-spin h-8 w-8 text-purple-600" />
            </div>
          )}

          {catalogError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{catalogError}</p>
            </div>
          )}

          {!catalogLoading && curatedImages.length === 0 && !catalogError && (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Package size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No curated images</h3>
              <p className="text-gray-600">
                {user?.isAdmin ? 'Add your first curated Docker image.' : 'No images available yet.'}
              </p>
            </div>
          )}

          {!catalogLoading && curatedImages.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {curatedImages.map(image => (
                <div key={image.id} className="bg-white rounded-lg shadow border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-lg text-gray-900">{image.name}</h3>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-300 flex items-center space-x-1">
                        <Package size={9} />
                        <span>Curated</span>
                      </span>
                      {image.tcf_status === 'completed' && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800 border border-green-300 flex items-center space-x-1">
                          <Zap size={9} />
                          <span>TCF Ready</span>
                        </span>
                      )}
                      {image.tcf_status === 'converting' && (() => {
                        const progress = conversionProgress[image.id];
                        const stepLabel = progress?.status === 'pulling' ? 'Pulling image...'
                          : progress?.status === 'extracting_openapi' ? 'Extracting OpenAPI...'
                          : progress?.status === 'extracting_weights' ? 'Extracting weights...'
                          : progress?.status === 'building' ? 'Building TCF image...'
                          : progress?.status === 'pushing' ? 'Pushing to registry...'
                          : 'Queued...';
                        return (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 border border-yellow-300 flex items-center space-x-1 animate-pulse">
                            <RefreshCw size={9} className="animate-spin" />
                            <span>{stepLabel}</span>
                          </span>
                        );
                      })()}
                      {image.tcf_status === 'failed' && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-800 border border-red-300 flex items-center space-x-1">
                          <AlertCircle size={9} />
                          <span>Failed</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      {getVisibilityIcon(image.visibility)}
                      <span className="text-xs text-gray-500 capitalize">{image.visibility === 'semi_private' ? 'Shared' : image.visibility}</span>
                    </div>
                  </div>

                  {image.description && (
                    <p className="text-sm text-gray-600 mb-3">{image.description}</p>
                  )}

                  <div className="bg-gray-50 rounded px-3 py-2 mb-3">
                    <code className="text-xs text-gray-700 break-all">{image.docker_image}</code>
                  </div>

                  {image.tcf_image && (
                    <div className="bg-green-50 rounded px-3 py-2 mb-3 border border-green-200">
                      <div className="flex items-center space-x-1 mb-1">
                        <Zap size={10} className="text-green-600" />
                        <span className="text-[10px] font-medium text-green-700">TCF Image</span>
                      </div>
                      <code className="text-xs text-green-800 break-all">{image.tcf_image}</code>
                    </div>
                  )}

                  {/* Pricing rates */}
                  {image.pricing_rates && typeof image.pricing_rates === 'object' && Object.keys(image.pricing_rates).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {image.pricing_rates.per_server_hour != null && (
                        <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200">${image.pricing_rates.per_server_hour}/hr</span>
                      )}
                      {image.pricing_rates.per_inference_run != null && (
                        <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">${image.pricing_rates.per_inference_run}/run</span>
                      )}
                      {image.pricing_rates.per_inference_minute != null && (
                        <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">${image.pricing_rates.per_inference_minute}/min</span>
                      )}
                      {image.pricing_rates.per_input_mb != null && (
                        <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded border border-yellow-200">${image.pricing_rates.per_input_mb}/MB in</span>
                      )}
                      {image.pricing_rates.per_output_mb != null && (
                        <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded border border-yellow-200">${image.pricing_rates.per_output_mb}/MB out</span>
                      )}
                    </div>
                  )}

                  {/* Details button (visible to all users) */}
                  {!user?.isAdmin && (
                    <div className="flex space-x-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => toggleDetails(image.id)}
                        className="text-gray-500 hover:text-purple-600 transition-colors text-xs flex items-center space-x-1"
                      >
                        {expandedDetails === image.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        <HardDrive size={12} />
                        <span>Details</span>
                      </button>
                      {image.openapi_schema && (
                        <button
                          onClick={() => setViewingOpenApi(image)}
                          className="text-gray-500 hover:text-blue-600 transition-colors text-xs flex items-center space-x-1"
                        >
                          <FileText size={12} />
                          <span>API Spec</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Admin actions */}
                  {user?.isAdmin && (
                    <div className="flex space-x-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => toggleDetails(image.id)}
                        className="text-gray-500 hover:text-purple-600 transition-colors text-xs flex items-center space-x-1"
                      >
                        {expandedDetails === image.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        <HardDrive size={12} />
                        <span>Details</span>
                      </button>
                      <button
                        onClick={() => { setEditingImage(image); setShowImageForm(true); }}
                        className="text-gray-500 hover:text-purple-600 transition-colors text-xs flex items-center space-x-1"
                      >
                        <Edit2 size={12} />
                        <span>Edit</span>
                      </button>
                      {image.visibility === 'semi_private' && (
                        <button
                          onClick={() => setSharingImage(image)}
                          className="text-gray-500 hover:text-orange-600 transition-colors text-xs flex items-center space-x-1"
                        >
                          <Users size={12} />
                          <span>Share</span>
                        </button>
                      )}
                      {!image.tcf_status && (
                        <button
                          onClick={() => handleConvertToTcf(image.id)}
                          className="text-gray-500 hover:text-green-600 transition-colors text-xs flex items-center space-x-1"
                        >
                          <Zap size={12} />
                          <span>Convert to TCF</span>
                        </button>
                      )}
                      {(image.tcf_status === 'failed' || image.tcf_status === 'completed') && (
                        <button
                          onClick={() => handleConvertToTcf(image.id)}
                          className="text-gray-500 hover:text-yellow-600 transition-colors text-xs flex items-center space-x-1"
                        >
                          <RefreshCw size={12} />
                          <span>{image.tcf_status === 'failed' ? 'Retry' : 'Re-convert'}</span>
                        </button>
                      )}
                      {image.openapi_schema && (
                        <button
                          onClick={() => setViewingOpenApi(image)}
                          className="text-gray-500 hover:text-blue-600 transition-colors text-xs flex items-center space-x-1"
                        >
                          <FileText size={12} />
                          <span>API Spec</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteImage(image.id)}
                        className="text-gray-500 hover:text-red-600 transition-colors text-xs flex items-center space-x-1 ml-auto"
                      >
                        <Trash2 size={12} />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}

                  {/* Expanded details: extracted weights */}
                  {expandedDetails === image.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <HardDrive size={14} className="text-purple-600" />
                        <span className="text-xs font-semibold text-gray-700 uppercase">Extracted Weights</span>
                      </div>
                      {!imageWeights[image.id] ? (
                        <div className="flex items-center space-x-2 py-3 text-xs text-gray-400">
                          <RefreshCw size={12} className="animate-spin" />
                          <span>Loading...</span>
                        </div>
                      ) : imageWeights[image.id].length === 0 ? (
                        <p className="text-xs text-gray-400 py-2">No weights associated with this image</p>
                      ) : (
                        <div className="space-y-1.5">
                          {imageWeights[image.id].map((w, i) => (
                            <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-xs">
                              <div className="flex items-center space-x-2 min-w-0">
                                <Package size={12} className="text-gray-400 shrink-0" />
                                <span className="font-medium text-gray-800 truncate" title={w.display_name || w.filename}>
                                  {w.display_name || w.filename}
                                </span>
                                <span className="text-gray-400">{w.sizeFormatted}</span>
                                {w.username && (
                                  <span className="text-gray-400">by {w.username}</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-1 shrink-0 ml-2">
                                <code className="text-[10px] font-mono text-gray-400 truncate max-w-[180px]" title={w.url}>
                                  {w.url}
                                </code>
                                <button
                                  onClick={() => copyWeightUrl(w.url)}
                                  className="p-1 text-gray-400 hover:text-purple-600 rounded"
                                  title="Copy r2:// URL"
                                >
                                  {weightsCopied === w.url ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                                </button>
                              </div>
                            </div>
                          ))}
                          <p className="text-[10px] text-gray-400 mt-1">
                            Total: {formatBytes(imageWeights[image.id].reduce((sum, w) => sum + (w.size || 0), 0))}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* My Registries Tab */}
      {activeTab === 'browse' && (
        <div className="space-y-6">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center space-x-2">
              <Server size={14} />
              <span>My Registries</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {registries.map(reg => (
                <button
                  key={reg.id}
                  onClick={() => browseRegistry(reg)}
                  className={`px-3 py-1.5 rounded-lg text-sm flex items-center space-x-2 transition-colors ${
                    selectedRegistry?.id === reg.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Server size={14} />
                  <span>{reg.name}</span>
                  <span className={`text-xs ${selectedRegistry?.id === reg.id ? 'text-purple-200' : 'text-gray-500'}`}>
                    {reg.server}
                  </span>
                </button>
              ))}
              {isAuthenticated ? (
                <button
                  onClick={() => browseDockerHub()}
                  className={`px-3 py-1.5 rounded-lg text-sm flex items-center space-x-2 transition-colors ${
                    selectedRegistry?.id === 'dockerhub'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Package size={14} />
                  <span>Docker Hub</span>
                </button>
              ) : (
                <span className="px-3 py-1.5 rounded-lg text-xs text-gray-400 bg-gray-50 flex items-center space-x-2">
                  <Package size={14} />
                  <span>Docker Hub — connect in Settings</span>
                </span>
              )}
            </div>

              {registryLoading && (
                <div className="flex justify-center py-6">
                  <RefreshCw className="animate-spin h-6 w-6 text-purple-600" />
                </div>
              )}

              {registryError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <pre className="text-red-800 text-sm whitespace-pre-wrap break-all">{registryError}</pre>
                </div>
              )}

              {!registryLoading && !registryError && selectedRegistry && selectedRegistry.id !== 'dockerhub' && registryRepos.length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">No repositories found in this registry.</div>
              )}

              {!registryLoading && registryRepos.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">{registryRepos.length} repositories in {selectedRegistry?.name}</p>
                  <div className="grid gap-3">
                    {registryRepos.map(repo => (
                      <div key={repo} className="bg-white p-4 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{repo}</h3>
                            <p className="text-xs text-gray-500 font-mono mt-1">{selectedRegistry.server}/{repo}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {user?.isAdmin && (
                              <button
                                onClick={() => addRegistryImageToCatalog(repo)}
                                className="bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 transition-colors flex items-center space-x-1"
                                title="Add to Curated Catalog"
                              >
                                <Plus size={14} />
                                <span>Catalog</span>
                              </button>
                            )}
                            <div className="relative">
                              <button
                                onClick={() => toggleRegistryDropdown(selectedRegistry.id, repo)}
                                className="border border-gray-300 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-50 transition-colors flex items-center space-x-1"
                              >
                                <Tag size={14} />
                                <span>Tags</span>
                                <ChevronDown size={14} className={`transform transition-transform ${openRegistryDropdown === repo ? 'rotate-180' : ''}`} />
                              </button>
                              {openRegistryDropdown === repo && (
                                <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                  <div className="max-h-48 overflow-y-auto p-2">
                                    {registryTags[repo]?.loading ? (
                                      <div className="py-2 px-2 text-sm text-gray-500">Loading tags...</div>
                                    ) : registryTags[repo]?.error ? (
                                      <div className="py-2 px-2 text-sm text-red-500">{registryTags[repo].error}</div>
                                    ) : Array.isArray(registryTags[repo]) && registryTags[repo].length > 0 ? (
                                      registryTags[repo].map(tag => (
                                        <div key={tag} className="py-1 px-2 hover:bg-blue-50 rounded text-sm font-mono cursor-pointer flex justify-between items-center" onClick={() => {
                                          navigator.clipboard.writeText(`${selectedRegistry.server}/${repo}:${tag}`);
                                        }}>
                                          <div>
                                            <span>{tag}</span>
                                            <span className="text-[10px] text-gray-400 ml-2">(copy)</span>
                                          </div>
                                          {user?.isAdmin && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); addRegistryImageToCatalog(repo, tag); setOpenRegistryDropdown(null); }}
                                              className="text-purple-600 hover:text-purple-800 ml-2"
                                              title="Add to catalog with this tag"
                                            >
                                              <Plus size={12} />
                                            </button>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="py-2 px-2 text-sm text-gray-500">No tags available</div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRegistry?.id === 'dockerhub' && (
              <>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            setCurrentPage(1);
                            searchDockerHub(1, true);
                          }
                        }}
                        placeholder="Search Docker Hub repositories..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                    <button
                      onClick={() => { setCurrentPage(1); searchDockerHub(1, true); }}
                      disabled={loading || !searchQuery.trim()}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      Search
                    </button>
                    <button
                      onClick={() => { setCurrentPage(1); loadUserRepositories(1, true); }}
                      disabled={loading}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                    >
                      <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                      <span>My Repos</span>
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-gray-600 mr-2">Popular:</span>
                    {['nginx', 'python', 'node', 'postgres', 'redis', 'mysql', 'ubuntu', 'alpine'].map(tag => (
                      <button
                        key={tag}
                        onClick={() => {
                          setSearchQuery(tag);
                          setCurrentPage(1);
                          setTimeout(() => searchDockerHub(1, true), 0);
                        }}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>

                  {dockerHubRepositories.length > 0 && (
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">Sort by:</span>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="relevance">Relevance</option>
                        <option value="stars">Stars</option>
                        <option value="pulls">Pull Count</option>
                        <option value="updated">Last Updated</option>
                        <option value="name">Name</option>
                      </select>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">{error}</p>
                  </div>
                )}

                {loading && (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="animate-spin h-8 w-8 text-purple-600" />
                  </div>
                )}

                <div className="grid gap-3">
                  {sortRepositories(dockerHubRepositories, sortBy).map(repo => (
                    <div key={repo.name} className="bg-white p-4 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-lg">{repo.name}</h3>
                            <a href={`https://hub.docker.com/r/${repo.name}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                              <ExternalLink size={16} />
                            </a>
                          </div>
                          {repo.description && <p className="text-gray-600 mt-1">{repo.description}</p>}
                          <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                            {repo.star_count && (
                              <div className="flex items-center space-x-1">
                                <Star size={14} /><span>{repo.star_count.toLocaleString()}</span>
                              </div>
                            )}
                            {repo.pull_count && (
                              <div className="flex items-center space-x-1">
                                <Download size={14} /><span>{repo.pull_count.toLocaleString()} pulls</span>
                              </div>
                            )}
                            {repo.last_updated && (
                              <div className="flex items-center space-x-1">
                                <Calendar size={14} /><span>Updated {formatDate(repo.last_updated)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {user?.isAdmin && (
                            <button
                              onClick={() => addDockerHubImageToCatalog(repo.name)}
                              className="bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 transition-colors flex items-center space-x-1"
                              title="Add to Curated Catalog"
                            >
                              <Plus size={14} />
                              <span>Catalog</span>
                            </button>
                          )}
                          <div className="relative">
                            <button
                              onClick={() => toggleDropdown(repo)}
                              className="border border-gray-300 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-50 transition-colors flex items-center space-x-1"
                            >
                              <Tag size={14} />
                              <span>Tags</span>
                              <ChevronDown size={14} className={`transform transition-transform ${openDropdown === repo.name ? 'rotate-180' : ''}`} />
                            </button>
                            {openDropdown === repo.name && (
                              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                <div className="max-h-48 overflow-y-auto p-2">
                                  {repoTags[repo.name] ? (
                                    repoTags[repo.name].error ? (
                                      <div className="py-2 px-2 text-sm text-gray-500">
                                        {repoTags[repo.name].error === 'private' ? 'Private repository - login required' :
                                         repoTags[repo.name].error === 'notfound' ? 'Repository not found' : 'Error loading tags'}
                                      </div>
                                    ) : Array.isArray(repoTags[repo.name]) && repoTags[repo.name].length > 0 ? (
                                      repoTags[repo.name].map((tag, index) => (
                                        <div key={index} className="py-1 px-2 hover:bg-blue-50 rounded text-sm font-mono cursor-pointer flex justify-between items-center" onClick={() => {
                                          navigator.clipboard.writeText(`${repo.name}:${tag.name}`);
                                        }}>
                                          <div>
                                            <span>{tag.name}</span>
                                            <span className="text-[10px] text-gray-400 ml-2">(copy)</span>
                                          </div>
                                          {user?.isAdmin && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); addDockerHubImageToCatalog(repo.name, tag.name); setOpenDropdown(null); }}
                                              className="text-purple-600 hover:text-purple-800 ml-2"
                                              title="Add to catalog with this tag"
                                            >
                                              <Plus size={12} />
                                            </button>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="py-2 px-2 text-sm text-gray-500">No tags available</div>
                                    )
                                  ) : (
                                    <div className="py-2 px-2 text-sm text-gray-500">Loading tags...</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {dockerHubRepositories.length > 0 && (hasNextPage || hasPreviousPage) && (
                  <div className="flex justify-between items-center py-6">
                    <div className="text-sm text-gray-500">
                      Showing {dockerHubRepositories.length} of {totalCount.toLocaleString()} repositories
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          if (currentMode === 'search') searchDockerHub(currentPage - 1, true);
                          else loadUserRepositories(currentPage - 1, true);
                        }}
                        disabled={!hasPreviousPage || loading}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >Previous</button>
                      <span className="px-4 py-2 text-sm text-gray-700">Page {currentPage}</span>
                      <button
                        onClick={() => {
                          if (currentMode === 'search') searchDockerHub(currentPage + 1, true);
                          else loadUserRepositories(currentPage + 1, true);
                        }}
                        disabled={!hasNextPage || loading}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >Next</button>
                    </div>
                  </div>
                )}

                {dockerHubRepositories.length > 0 && hasNextPage && (
                  <div className="text-center py-4">
                    <button
                      onClick={() => {
                        if (currentMode === 'search') searchDockerHub(currentPage + 1, false);
                        else loadUserRepositories(currentPage + 1, false);
                      }}
                      disabled={loading}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Loading...' : 'Load More Results'}
                    </button>
                  </div>
                )}

                {!loading && dockerHubRepositories.length === 0 && !error && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {searchQuery ? 'No repositories found.' : 'Load your repositories or search Docker Hub to get started.'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Image Form Modal */}
      {showImageForm && (
        <ImageFormModal
          image={editingImage}
          prefill={!editingImage ? prefillData : null}
          onClose={() => { setShowImageForm(false); setEditingImage(null); setPrefillData(null); }}
          onSave={editingImage ? handleUpdateImage : handleCreateImage}
        />
      )}

      {/* Image Sharing Modal */}
      {sharingImage && (
        <ImageSharingModal
          image={sharingImage}
          onClose={() => setSharingImage(null)}
        />
      )}

      {/* OpenAPI Viewer Modal */}
      {viewingOpenApi && (
        <OpenApiViewer
          image={viewingOpenApi}
          onClose={() => setViewingOpenApi(null)}
        />
      )}
    </div>
  );
};

export default ImagePage;
